import { useEffect, useRef, useState, type JSX } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { streamChat } from "../lib/api";
import { CitationChip } from "../components/chat/CitationChip";
import { getBlockRenderer } from "../components/chat/blocks";
import { IconCopy, IconStop } from "../components/icons";
import type { Block, Citation } from "@shared/types";
import type { AskMode } from "./SelectionLayer";
import { stageAtLeast } from "./askStage";
import "../styles/chat.css"; // block renderers (markdown/resolution/compare/freeform) use chat.css

/**
 * In-doc ask dock (FEATURE_MANUAL_ASK). The same 小环 assistant, but the pinned
 * passage is sent as context and retrieval is scoped to THIS manual (manualSource
 * -> server-side search_kb scope). Answer renders as markdown + citation chips;
 * a chip that points back into the manual jumps the reader to that page.
 */

export interface PendingAsk {
  passage: string;
  mode: AskMode;
  /** Monotonic key so re-selecting the same passage re-triggers the effect. */
  key: number;
}

interface DockTurn {
  role: "user" | "assistant";
  /** Plain-text answer (non-structured mode); empty when the answer is blocks. */
  text: string;
  /** Structured answer blocks (FEATURE_IA_STRUCTURED_ANSWER) — rendered via the registry. */
  blocks?: Block[];
  citations?: Citation[];
  /** #08 (user turns, stage 1b+): which passage this question was asked about. */
  source?: { label: string; key: number };
  /** #09: the stream errored — offer 重试 instead of a dead end. */
  error?: boolean;
  /** The question that produced this assistant turn (drives 重试). */
  question?: string;
  /** The passage + anchor key this turn was asked about (in-place retry reuses them). */
  passage?: string;
  sourceKey?: number;
}

interface Props {
  kbSource: string;
  manualTitle: string;
  pageTitle: string;
  /** Assistant identity — content from the manifest, never hardcoded (F1.2). */
  assistantName?: string | undefined;
  assistantAvatar?: string | undefined;
  ask: PendingAsk | null;
  onClose: () => void;
  /** Jump the reader back to a citation's source: page by docId (title
   *  fallback), then the section anchor when the chunk carries one (#03). */
  onJump: (citation: { title: string; source?: string; anchor?: string }) => void;
  /** Scroll the prose back to the anchored passage (#04); false = anchor gone. */
  onPinJump?: () => boolean;
  /** #08: scroll back to a specific turn's passage (keyed anchor). */
  onTurnJump?: (key: number) => boolean;
  /** Staged rollout slice (MANUAL_ASK_STAGE); 1b+ features gate on it. */
  stage?: string;
  /** #02 (stage 1b+): closed = hidden, not unmounted — the conversation survives. */
  hidden?: boolean;
  /**
   * R★.2 handoff: "看" stays in the manual, "做" goes back to the main line.
   * Called with a context-carrying message; the reader stashes it and navigates
   * to the main chat (the Copilot "Continue in Chat" pattern).
   */
  onHandoff?: ((message: string) => void) | undefined;
}

function questionFor(mode: AskMode, passage: string): string {
  if (mode === "explain") return `解释一下这段：「${passage}」`;
  if (mode === "deepen") return `就这段再深入讲讲：「${passage}」`;
  if (mode === "related") return `这本手册里有哪些和这段相关的内容？「${passage}」`;
  return ""; // "ask": the user types their own question
}

/** #08: a short readable tag of the passage a question was asked about. */
function sourceLabel(passage: string): string {
  const flat = passage.replace(/\s+/g, " ").trim();
  return flat.length > 14 ? `${flat.slice(0, 14)}…` : flat;
}

export function AskDock({
  kbSource, manualTitle, pageTitle, ask, onClose, onJump, onPinJump, onTurnJump, stage, hidden, onHandoff,
  assistantName, assistantAvatar
}: Props): JSX.Element {
  const assistant = assistantName ?? "小环";
  const avatar = assistantAvatar ?? "环";
  const [turns, setTurns] = useState<DockTurn[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  // The pinned quote FREEZES the passage and the page it came from — the reader
  // may navigate to another page while the dock stays open, and the live
  // pageTitle prop would then mislabel the quote.
  const [pinned, setPinned] = useState<{ passage: string; page: string } | null>(null);
  const sessionIdRef = useRef<string>(`guides-ask-${Math.floor(performance.now())}`);
  const abortRef = useRef<AbortController | null>(null);
  const passageRef = useRef<string>("");
  const passageKeyRef = useRef<number>(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const atLeast1b = stageAtLeast(stage, "1b");
  const atLeast1c = stageAtLeast(stage, "1c");
  const atLeast2 = stageAtLeast(stage, "2");
  // #10-② (stage 2): the retrieval scope is switchable — 本手册 pins kbSource,
  // 全部文档 omits it (the backend already treats "no manualSource" as全库).
  const [scope, setScope] = useState<"manual" | "all">("manual");

  // #12 (stage 1c+): opening the dock moves focus into the composer.
  useEffect(() => {
    if (atLeast1c && hidden !== true) inputRef.current?.focus();
  }, [atLeast1c, hidden]);

  /**
   * Core sender. Normally appends a user+assistant turn pair; with
   * `replaceIndex` it retries IN PLACE (the failed assistant turn is reset and
   * re-streamed with its ORIGINAL passage — #09 review fix, no duplicate pair).
   * Every stream patches ITS OWN assistant turn, and only the CURRENT stream
   * (abortRef owner) may flip the shared `streaming` flag — a rapid re-ask
   * can no longer have the dying stream shut down the new one's indicator.
   */
  function sendCore(rawQuestion: string, passage: string, sourceKey: number, replaceIndex?: number): void {
    // Pin the selected passage as context; scope keeps the answer in-manual.
    const message = passage
      ? `关于手册里这段：「${passage}」\n\n${rawQuestion}`
      : rawQuestion;
    // The index of the assistant turn this stream owns (captured synchronously
    // in the updater; idempotent under StrictMode double-invoke).
    let aIdx = -1;
    setTurns((t) => {
      const next = [...t];
      const fresh: DockTurn = { role: "assistant", text: "", question: rawQuestion, passage, sourceKey };
      if (replaceIndex !== undefined && next[replaceIndex]?.role === "assistant") {
        next[replaceIndex] = fresh;
        aIdx = replaceIndex;
      } else {
        next.push({
          role: "user",
          text: rawQuestion,
          ...(passage ? { source: { label: sourceLabel(passage), key: sourceKey } } : {})
        });
        next.push(fresh);
        aIdx = next.length - 1;
      }
      return next;
    });
    setStreaming(true);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const owner = (): boolean => abortRef.current === ctrl;
    let acc = "";
    let cites: Citation[] = [];
    // Structured answers (FEATURE_IA_STRUCTURED_ANSWER) arrive as typed blocks via
    // onBlock (markdown / resolution / compare / freeform / …), NOT delta text —
    // collect them (deduped by id, since they re-emit idempotently) and render
    // through the SAME registry the main chat uses. Plain-text answers use `acc`.
    const blocks = new Map<string, Block>();
    const setAssistant = (patch: Partial<DockTurn>): void =>
      setTurns((t) => {
        const next = [...t];
        const i = aIdx >= 0 && aIdx < next.length && next[aIdx]?.role === "assistant" ? aIdx : -1;
        if (i >= 0) next[i] = { ...next[i], ...patch } as DockTurn;
        return next;
      });
    void streamChat(
      sessionIdRef.current,
      message,
      {
        onStart: () => undefined,
        onDelta: (d) => {
          acc += d;
          setAssistant({ text: acc });
        },
        onTool: () => undefined,
        onCitations: (c) => {
          cites = c;
          setAssistant({ citations: c });
        },
        onWidget: () => undefined,
        onBlock: (block) => {
          blocks.set(block.id, block);
          setAssistant({ blocks: [...blocks.values()] });
        },
        onRunnable: () => undefined, // in-doc ask has no "run in your 织灵" CTA
        onDone: ({ fullText }) => {
          // Prefer structured blocks; fall back to streamed/plain text.
          if (blocks.size > 0) setAssistant({ blocks: [...blocks.values()], citations: cites });
          else setAssistant({ text: acc || fullText, citations: cites });
          if (owner()) setStreaming(false);
        },
        onError: (msg) => {
          // #09: a user-initiated stop is not an error — keep the partial text.
          // Stopped before ANY content: mark the turn so 重试 is offered instead
          // of a ghost "小环正在查…" that never resolves.
          if (ctrl.signal.aborted) {
            if (blocks.size === 0 && acc.length === 0) setAssistant({ text: "已停止。", error: true });
            if (owner()) setStreaming(false);
            return;
          }
          if (blocks.size === 0 && acc.length === 0) setAssistant({ text: `出错了：${msg}`, error: true });
          if (owner()) setStreaming(false);
        }
      },
      ctrl.signal,
      undefined,
      undefined,
      // #10-②: 本手册 pins retrieval to this manual; 全部文档 lifts the pin.
      scope === "manual" ? kbSource : undefined
    );
  }

  /** Ask about the CURRENTLY pinned passage (composer, popover, follow-up chips). */
  function send(rawQuestion: string): void {
    sendCore(rawQuestion, passageRef.current, passageKeyRef.current);
  }

  /**
   * R★.2: hand the conversation's context to the main chat (真执行 lives
   * there). The message shows as the USER's bubble, so it reads like a person
   * talking — not a system log about "carrying context".
   */
  function handoff(): void {
    const lastQ = [...turns].reverse().find((t) => t.role === "user")?.text ?? "";
    const passage = passageRef.current.slice(0, 200);
    const msg =
      `我正在读《${manualTitle}》` +
      (pinned ? `的「${pinned.page}」，其中提到：「${passage}」` : "") +
      (lastQ ? `。我刚问过：「${lastQ}」` : "") +
      `。想接着这个话题继续——需要动手的操作可以直接帮我执行。`;
    onHandoff?.(msg);
  }

  // A new selection/action: pin the passage; auto-ask for explain/deepen/related.
  useEffect(() => {
    if (!ask) return;
    passageRef.current = ask.passage;
    passageKeyRef.current = ask.key;
    setPinned({ passage: ask.passage, page: pageTitle });
    const q = questionFor(ask.mode, ask.passage);
    if (q) send(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask?.key]);

  // Keep the latest turn in view as the answer streams.
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [turns]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /** #09: copy an answer (blocks preferred, streamed text as fallback). */
  function copyTurn(t: DockTurn, i: number): void {
    const body =
      t.blocks && t.blocks.length > 0
        ? t.blocks.map((b) => ("markdown" in b && typeof b.markdown === "string" ? b.markdown : "")).filter(Boolean).join("\n\n")
        : t.text;
    void navigator.clipboard?.writeText(body).then(() => {
      setCopiedAt(i);
      setTimeout(() => setCopiedAt((c) => (c === i ? null : c)), 1600);
    });
  }

  return (
    <aside className={`guides-ask${hidden === true ? " hidden" : ""}`}>
      <header className="guides-ask-head">
        <span className="guides-ask-avatar" aria-hidden="true">{avatar}</span>
        <span className="guides-ask-name">{assistant}</span>
        {atLeast2 ? (
          // #10-②: the badge becomes a switch — its very shape says "there is
          // more than one scope", solving 不知道 and 切不了 in one control.
          <div className="guides-ask-scope-seg" role="group" aria-label="检索范围">
            <button
              type="button"
              className={scope === "manual" ? "on" : ""}
              aria-pressed={scope === "manual"}
              onClick={() => setScope("manual")}
            >
              本手册
            </button>
            <button
              type="button"
              className={scope === "all" ? "on" : ""}
              aria-pressed={scope === "all"}
              onClick={() => setScope("all")}
            >
              全部文档
            </button>
          </div>
        ) : (
          <span className="guides-ask-scope" title={`只在《${manualTitle}》范围内作答`}>范围 · 本手册</span>
        )}
        <button type="button" className="guides-ask-x" aria-label="关闭" onClick={onClose}>×</button>
      </header>

      {/* #12 (stage 1c+): screen readers hear the answer arrive. */}
      <div className="guides-ask-body" ref={bodyRef} aria-live={atLeast1c ? "polite" : undefined}>
        {/* #10-①: say the scope in plain words — users otherwise assume a
            universal assistant and misread "查不到" as the product being dumb. */}
        <div className="guides-ask-scope-note">
          {scope === "manual" ? (
            <>{assistant}现在<b>只看《{manualTitle}》</b>作答；问手册外的，请回主对话。</>
          ) : (
            <>{assistant}现在在<b>全部文档</b>范围内查找作答。</>
          )}
        </div>
        {pinned && (
          <button
            type="button"
            className="guides-ask-pinned"
            title="点按回到原文"
            onClick={() => onPinJump?.()}
          >
            <div className="guides-ask-pinned-lab">来自手册 · {pinned.page}</div>
            {pinned.passage}
          </button>
        )}
        {turns.map((t, i) =>
          t.role === "user" ? (
            <div key={i} className="guides-ask-u-wrap">
              {atLeast1b && t.source && (
                // #08: each question carries ITS passage — multi-turn asks about
                // different passages never mislabel history. Click scrolls back.
                <button
                  type="button"
                  className="guides-ask-u-src"
                  title="点按回到原文"
                  onClick={() => onTurnJump?.(t.source?.key ?? 0)}
                >
                  引自 · {t.source.label}
                </button>
              )}
              <div className="guides-ask-u">{t.text}</div>
            </div>
          ) : (
            <div key={i} className="guides-ask-a">
              <div className="guides-ask-a-body md">
                {t.blocks && t.blocks.length > 0 ? (
                  // Render structured answer blocks through the same registry the
                  // main chat uses, so any block type (markdown/resolution/compare/
                  // freeform/…) renders — not just markdown.
                  t.blocks.map((b) => {
                    const Renderer = getBlockRenderer(b.type);
                    return Renderer ? (
                      <Renderer
                        key={b.id}
                        block={b}
                        onSendPrompt={(text) => send(text)}
                        onMutateStep={() => undefined}
                        citations={t.citations ?? []}
                      />
                    ) : null;
                  })
                ) : t.text ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.text}</ReactMarkdown>
                ) : (
                  <span className="guides-ask-typing">{assistant}正在查这本手册…</span>
                )}
              </div>
              {t.citations && t.citations.length > 0 && (
                <div className="guides-ask-cites">
                  {t.citations
                    .filter((c) => c.lowConfidence !== true)
                    .map((c, ci) => (
                      <CitationChip
                        key={ci}
                        citation={c}
                        active={false}
                        onClick={() => onJump({ title: c.title, source: c.source, ...(c.anchor ? { anchor: c.anchor } : {}) })}
                      />
                    ))}
                </div>
              )}
              {/* R★.2 (#06 下半): deepen/related return as POST-ANSWER follow-up
                  chips — their position under this answer makes both their
                  meaning and the conversation's continuity self-evident. */}
              {atLeast2 && i === turns.length - 1 && !streaming && t.error !== true &&
                (t.text.length > 0 || (t.blocks && t.blocks.length > 0)) && (
                  <>
                    <div className="guides-ask-follow">
                      <button type="button" className="guides-ask-fchip" onClick={() => send(questionFor("deepen", passageRef.current))}>
                        再深入 →
                      </button>
                      <button
                        type="button"
                        className="guides-ask-fchip"
                        onClick={() =>
                          send(
                            // Wording follows the ACTIVE scope — "这本手册里"
                            // would contradict a 全部文档 search (#10-② nit).
                            scope === "manual"
                              ? questionFor("related", passageRef.current)
                              : `有哪些和这段相关的内容？「${passageRef.current}」`
                          )
                        }
                      >
                        找相关章节 →
                      </button>
                      <button type="button" className="guides-ask-fchip" onClick={() => inputRef.current?.focus()}>
                        就这段追问 →
                      </button>
                    </div>
                    {onHandoff && (
                      <button type="button" className="guides-ask-handoff" onClick={handoff}>
                        在{assistant}里继续（可真执行）→
                      </button>
                    )}
                  </>
                )}
              {/* #09 (stage 1b+): the chat basics — copy a good answer; retry a failed one. */}
              {atLeast1b && (t.text || (t.blocks && t.blocks.length > 0)) && !(streaming && i === turns.length - 1) && (
                <div className="guides-ask-a-tools">
                  {t.error === true ? (
                    <button
                      type="button"
                      className="guides-ask-tool"
                      onClick={() => {
                        // In-place retry with the turn's ORIGINAL passage — the
                        // current pin may have moved to another selection.
                        if (t.question && !streaming) sendCore(t.question, t.passage ?? "", t.sourceKey ?? 0, i);
                      }}
                    >
                      ↻ 重试
                    </button>
                  ) : (
                    <button type="button" className="guides-ask-tool" onClick={() => copyTurn(t, i)}>
                      <IconCopy size={12} /> {copiedAt === i ? "已复制" : "复制"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>

      <form
        className="guides-ask-composer"
        onSubmit={(e) => {
          e.preventDefault();
          const q = input.trim();
          if (!q || streaming) return;
          setInput("");
          send(q);
        }}
      >
        <input
          ref={inputRef}
          className="guides-ask-input"
          placeholder="就着这段继续问…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={streaming}
        />
        {atLeast1b && streaming ? (
          // #09: never trap the user in a runaway answer — the send slot becomes stop.
          <button
            type="button"
            className="guides-ask-send stop"
            aria-label="停止生成"
            title="停止生成"
            onClick={() => abortRef.current?.abort()}
          >
            <IconStop size={13} />
          </button>
        ) : (
          <button type="submit" className="guides-ask-send" disabled={streaming || input.trim().length === 0}>↑</button>
        )}
      </form>
    </aside>
  );
}
